import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import { CabTabs, CabList } from '@CabComponents';
import { IoClose } from 'react-icons/io5';
import { AppNotification } from '../../store/cabinetApi/generated/notifications';
import { CabButton } from '@CabComponents/CabButton';
import CabSpinner from '@CabComponents/CabSpinner';
import colors from '../../colors';
import { CabIcon } from '@CabComponents/CabIcon';


const tabs = [
  { label: 'Unread', id: 'unread' },
  { label: 'Read', id: 'read' },
];

const DRAWER_WIDTH = 624; // 480px * 1.3 = 624px

export interface NotificationsDrawerProps {
  open: boolean;
  unreadNotifications: AppNotification[] | undefined;
  readNotifications: AppNotification[] | undefined;
  unreadLoading: boolean;
  readLoading: boolean;
  unreadError: unknown;
  readError: unknown;
  loadingAction: number | null;
  onClose: () => void;
  onNotificationAction: (notification: AppNotification, action: string) => void;
  formatNotificationDate: (dateString: string) => string;
}

export const NotificationsDrawer = ({
  open,
  unreadNotifications,
  readNotifications,
  unreadLoading,
  readLoading,
  unreadError,
  readError,
  loadingAction,
  onClose,
  onNotificationAction,
  formatNotificationDate,
}: NotificationsDrawerProps) => {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        zIndex: 1400, // Higher than most MUI components to ensure it's on top
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          maxWidth: '90vw',
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: colors.black100,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            borderBottom: `1px solid ${colors.black200}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="h1" sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            Notifications
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: colors.black700,
              '&:hover': {
                backgroundColor: colors.black200,
              },
            }}
          >
            <CabIcon Icon={IoClose} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', padding: 2 }}>
          <CabTabs
            tabs={tabs}
            initValue={tabIndex}
            onChangeTab={setTabIndex}
            tabPanelSx={{ mx: 0 }}
          >
            <Box sx={{ width: '100%', mt: 2 }}>
              <NotificationList
                notifications={unreadNotifications}
                isLoading={unreadLoading}
                error={unreadError}
                loadingAction={loadingAction}
                onNotificationAction={onNotificationAction}
                formatNotificationDate={formatNotificationDate}
                emptyMessage="No unread notifications."
              />
            </Box>
            
            <Box sx={{ width: '100%', mt: 2 }}>
              <NotificationList
                notifications={readNotifications}
                isLoading={readLoading}
                error={readError}
                loadingAction={loadingAction}
                onNotificationAction={onNotificationAction}
                formatNotificationDate={formatNotificationDate}
                emptyMessage="No read notifications."
              />
            </Box>
          </CabTabs>
        </Box>
      </Box>
    </Drawer>
  );
};

interface NotificationListProps {
  notifications: AppNotification[] | undefined;
  isLoading: boolean;
  error: unknown;
  loadingAction: number | null;
  onNotificationAction: (notification: AppNotification, action: string) => void;
  formatNotificationDate: (dateString: string) => string;
  emptyMessage: string;
}

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  isLoading,
  error,
  loadingAction,
  onNotificationAction,
  formatNotificationDate,
  emptyMessage,
}) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200
        }}>
        <CabSpinner scale={2} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        Failed to load notifications. Please try again.
      </Typography>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <Typography color="textSecondary" align="center">
        {emptyMessage}
      </Typography>
    );
  }

  const listItems = notifications.map((notification) => {
    const isNotificationLoading = loadingAction === notification.id;
    
    // Determine primary action button based on notification type
    const getPrimaryActionButton = () => {
      if (notification.type === 'CADENCE_REMINDER') {
        return (
          <CabButton
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onNotificationAction(notification, 'accept');
            }}
            disabled={isNotificationLoading}
            icon={isNotificationLoading ? <CabSpinner scale={1} /> : undefined}
          >
            {isNotificationLoading ? 'Processing...' : 'Schedule'}
          </CabButton>
        );
      } else if (notification.type === 'CONTACT_TASK_DUE') {
        return (
          <CabButton
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onNotificationAction(notification, 'accept');
            }}
            disabled={isNotificationLoading}
            icon={isNotificationLoading ? <CabSpinner scale={1} /> : undefined}
          >
            {isNotificationLoading ? 'Processing...' : 'View Contact'}
          </CabButton>
        );
      } else if (notification.type === 'MEETING_LINK') {
        return (
          <CabButton
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onNotificationAction(notification, 'accept');
            }}
            disabled={isNotificationLoading}
            icon={isNotificationLoading ? <CabSpinner scale={1} /> : undefined}
          >
            {isNotificationLoading ? 'Processing...' : 'Open'}
          </CabButton>
        );
      }
      return null;
    };

    const primaryActionButton = getPrimaryActionButton();
    
    // Create custom secondary action with primary button and Done button stacked
    const customSecondaryAction = (
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
          pr: 1,
        }}
      >
        {primaryActionButton && (
          <Box 
            onClick={(e) => e.stopPropagation()}
            sx={{ 
              width: '100%',
              '& > *': {
                width: '100%',
              },
            }}
          >
            {primaryActionButton}
          </Box>
        )}
        {!notification.read && (
          <Box sx={{ width: primaryActionButton ? '100%' : 'auto' }}>
            <CabButton
              buttonType="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onNotificationAction(notification, 'done');
              }}
              sx={{ 
                fontSize: '0.9375rem',
                width: '100%',
              }}
            >
              Dismiss
            </CabButton>
          </Box>
        )}
      </Box>
    );
    
    return {
      key: notification.id,
      value: notification.id,
      editable: true,
      customSecondaryAction: customSecondaryAction,
      label: (
        <Box sx={{ width: '100%', pr: primaryActionButton ? 12 : 6 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: notification.read ? 400 : 600,
              color: notification.read ? colors.black700 : colors.black900,
              mb: 0.5
            }}>
            {notification.title}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: colors.black700,
              mb: 0.5,
            }}
          >
            {notification.body}
          </Typography>
          <Typography
            variant="body2"
            sx={{ 
              color: colors.black500,
            }}
          >
            {formatNotificationDate(notification.created_at)}
          </Typography>
        </Box>
      ),
    };
  });

  return <CabList items={listItems} />;
};
